import React, { useState, useEffect } from "react";
import apiClient from "../utils/apiClient";
import { X, Pencil, RefreshCw, Trash2 } from "lucide-react";
import server from "../utils/serverConfig";
import toast from "react-hot-toast";
import ExitSuccessModal from "./ExitSuccessModal";
import { useGlobalData } from "../hooks/useGlobalHooks";

/**
 * AlpacaBulkModifyModal Component
 * Dedicated bulk/single modification modal specifically for Alpaca orders.
 */
export function AlpacaBulkModifyModal({ isOpen, onClose, orders = [], onSuccess }) {
    const { users } = useGlobalData();
    const [modifyData, setModifyData] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [submittedOrders, setSubmittedOrders] = useState([]);
    const [bulkFields, setBulkFields] = useState({
        limitPrice: "",
        stopPrice: "",
        qty: "",
        orderType: ""
    });

    const formatPrice = (p) => {
        if (p === undefined || p === null || p === 0 || p === "0" || p === "0.00") return "-";
        return Number(p).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Initialize modify data when modal opens
    useEffect(() => {
        if (isOpen && orders && orders.length > 0) {
            setModifyData(orders.map(order => {
                let oType = order.orderType || order.order_type || order.type || order.OrderType;
                if (oType === 4 || oType === "4") oType = "STOPLIMIT";
                else if (oType === 3 || oType === "3") oType = "STOP";
                else if (oType === 2 || oType === "2") oType = "MARKET";
                else if (oType === 1 || oType === "1") oType = "LIMIT";

                const lp = Number(order.limit_price || order.limitPrice || order.price || 0);
                const sp = Number(order.stop_price || order.stopPrice || order.triggerPrice || 0);

                let finalOType = String(oType || "").toUpperCase();
                if (!finalOType || finalOType === "LIMIT") {
                    if (lp > 0 && sp > 0) finalOType = "STOPLIMIT";
                    else if (sp > 0) finalOType = "STOP";
                    else finalOType = "LIMIT";
                }

                return {
                    orderId: String(order.order_id || order.orderId),
                    clientId: String(order.clientId || order.client_id),
                    broker: "alpaca",
                    symbol: order.symbol || "N/A",
                    strategy: order.strategy || "Manual",
                    qty: Math.abs(parseFloat(order.qty || 0)),
                    limitPrice: lp > 0 ? lp.toFixed(2) : "",
                    stopPrice: sp > 0 ? sp.toFixed(2) : "",
                    orderType: finalOType,
                    status: 'pending'
                };
            }));
        }
    }, [isOpen, orders]);

    const handleInputChange = (index, field, value) => {
        const newData = [...modifyData];
        newData[index] = { ...newData[index], [field]: value };
        setModifyData(newData);
    };

    const handleRemoveItem = (index) => {
        setModifyData(prev => {
            const next = prev.filter((_, idx) => idx !== index);
            if (next.length === 0) onClose();
            return next;
        });
        toast.success("Order removed from batch");
    };

    const applyBulk = (field) => {
        const val = bulkFields[field];
        if (val === "" || val === undefined) return;

        setModifyData(prev => prev.map(item => ({ ...item, [field]: val })));
        toast.success(`Applied ${field} to all selected Alpaca orders`);
    };

    const handleSubmit = async () => {
        if (!modifyData.length) return;
        setSubmitting(true);

        try {
            const alpacaPayload = modifyData.map(item => ({
                orderid: item.orderId,
                clientid: item.clientId,
                broker: "alpaca",
                qty: Number(item.qty),
                ...(item.limitPrice !== "" && Number(item.limitPrice) > 0 ? { limitPrice: Number(item.limitPrice) } : {}),
                ...(item.stopPrice !== "" && Number(item.stopPrice) > 0 ? { stopPrice: Number(item.stopPrice) } : {}),
                orderType: item.orderType,
                symbol: item.symbol,
                strategy: item.strategy
            }));

            const response = await apiClient.post(`${server.godseye.baseUrl}modify-order`, alpacaPayload);
            const resData = response.data;

            if (resData.status === 200 || resData.status === 207) {
                const rawItems = resData.data || [];
                const formattedOrders = rawItems.map(o => {
                    const original = modifyData.find(m => String(m.orderId) === String(o.order_id || o.orderId));
                    const isSuccess = o.status === "success";
                    return {
                        ...o,
                        orderId: o.order_id || o.orderId,
                        clientId: o.client_id || o.clientId,
                        symbol: original?.symbol || "N/A",
                        broker: "alpaca",
                        status: isSuccess ? "PENDING" : "Rejected",
                        rejectReason: o.response?.message || o.message || (isSuccess ? "" : "Modification failed")
                    };
                });

                toast.success(resData.message || "Alpaca order modification processed.");
                if (onSuccess) onSuccess(formattedOrders);
                onClose();
            } else {
                toast.error(resData.message || "Alpaca order modification failed.");
            }
        } catch (err) {
            console.error("❌ Alpaca order modification error:", err);
            toast.error("Failed to modify Alpaca orders: " + (err.response?.data?.message || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-[70] backdrop-blur-sm p-4 text-xs">
            <div className="bg-[#1a1d29] rounded-xl border border-gray-700 w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-700/50 flex justify-between items-center bg-[#14161f]">
                    <div>
                        <h2 className="text-white text-base font-bold flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span>
                            Alpaca Order Modification ({modifyData.length} order{modifyData.length > 1 ? "s" : ""})
                        </h2>
                        <p className="text-gray-400 text-[11px] mt-0.5">Modify parameters for active Alpaca orders</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-full text-gray-400 transition-colors">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Bulk Inputs Header */}
                <div className="px-6 py-3 bg-[#14161f]/50 border-b border-gray-800 flex items-center justify-between gap-4">
                    <span className="text-gray-400 text-[11px] font-semibold uppercase tracking-wider">Bulk Fill:</span>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                placeholder="Qty"
                                value={bulkFields.qty}
                                onChange={(e) => setBulkFields(prev => ({ ...prev, qty: e.target.value }))}
                                className="w-16 bg-[#1a1d29] border border-gray-700 text-white text-[11px] rounded px-2 py-1 focus:outline-none focus:border-blue-500 font-mono"
                            />
                            <button onClick={() => applyBulk("qty")} className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-[10px] font-medium transition">Set</button>
                        </div>
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                step="0.01"
                                placeholder="Limit Price"
                                value={bulkFields.limitPrice}
                                onChange={(e) => setBulkFields(prev => ({ ...prev, limitPrice: e.target.value }))}
                                className="w-20 bg-[#1a1d29] border border-gray-700 text-white text-[11px] rounded px-2 py-1 focus:outline-none focus:border-blue-500 font-mono"
                            />
                            <button onClick={() => applyBulk("limitPrice")} className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-[10px] font-medium transition">Set</button>
                        </div>
                        <div className="flex items-center gap-1">
                            <input
                                type="number"
                                step="0.01"
                                placeholder="Stop Price"
                                value={bulkFields.stopPrice}
                                onChange={(e) => setBulkFields(prev => ({ ...prev, stopPrice: e.target.value }))}
                                className="w-20 bg-[#1a1d29] border border-gray-700 text-white text-[11px] rounded px-2 py-1 focus:outline-none focus:border-blue-500 font-mono"
                            />
                            <button onClick={() => applyBulk("stopPrice")} className="bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded text-[10px] font-medium transition">Set</button>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="flex-1 overflow-auto p-4">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#0f1117] text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                            <tr className="border-b border-gray-800">
                                <th className="py-2.5 px-3">Client</th>
                                <th className="py-2.5 px-3">Symbol</th>
                                <th className="py-2.5 px-3 text-center">Type</th>
                                <th className="py-2.5 px-3 text-center">Qty</th>
                                <th className="py-2.5 px-3 text-center">Limit Price ($)</th>
                                <th className="py-2.5 px-3 text-center">Stop Price ($)</th>
                                <th className="py-2.5 px-3 text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800/50 text-gray-300">
                            {modifyData.map((item, idx) => (
                                <tr key={idx} className="hover:bg-white/[0.01]">
                                    <td className="py-2.5 px-3 font-semibold text-white font-mono">{item.clientId}</td>
                                    <td className="py-2.5 px-3 font-bold text-blue-400">{item.symbol}</td>
                                    <td className="py-2.5 px-3 text-center">
                                        <select
                                            value={item.orderType}
                                            onChange={(e) => handleInputChange(idx, "orderType", e.target.value)}
                                            className="bg-[#14161f] border border-gray-700 text-gray-200 text-[10px] rounded px-2 py-1 focus:outline-none"
                                        >
                                            <option value="LIMIT">LIMIT</option>
                                            <option value="STOP">STOP</option>
                                            <option value="STOPLIMIT">STOP LIMIT</option>
                                            <option value="MARKET">MARKET</option>
                                        </select>
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                        <input
                                            type="number"
                                            value={item.qty}
                                            onChange={(e) => handleInputChange(idx, "qty", e.target.value)}
                                            className="w-16 bg-[#14161f] border border-gray-700 text-white font-mono text-[11px] rounded px-2 py-1 text-center focus:outline-none focus:border-blue-500"
                                        />
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={item.limitPrice}
                                            onChange={(e) => handleInputChange(idx, "limitPrice", e.target.value)}
                                            className="w-20 bg-[#14161f] border border-gray-700 text-white font-mono text-[11px] rounded px-2 py-1 text-center focus:outline-none focus:border-blue-500"
                                            placeholder="-"
                                        />
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={item.stopPrice}
                                            onChange={(e) => handleInputChange(idx, "stopPrice", e.target.value)}
                                            className="w-20 bg-[#14161f] border border-gray-700 text-white font-mono text-[11px] rounded px-2 py-1 text-center focus:outline-none focus:border-blue-500"
                                            placeholder="-"
                                        />
                                    </td>
                                    <td className="py-2.5 px-3 text-center">
                                        <button
                                            onClick={() => handleRemoveItem(idx)}
                                            className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                                            title="Remove order from batch"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-700/50 bg-[#14161f] flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 font-semibold transition"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={submitting}
                        className="px-5 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold transition flex items-center gap-2 shadow-lg shadow-blue-900/30 disabled:opacity-50"
                    >
                        {submitting ? (
                            <>
                                <RefreshCw className="w-4 h-4 animate-spin" /> Modifying...
                            </>
                        ) : (
                            <>
                                <Pencil className="w-4 h-4" /> Confirm Modification
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
